import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Power } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const empty = { name: "", email: "", password: "" };

export default function AdminWaiters() {
  const { user } = useAuth();
  const expired = user?.subscription_active === false;
  const [waiters, setWaiters] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try { const { data } = await api.get("/admin/waiters"); setWaiters(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/waiters", form);
      toast.success("Mesero creado");
      setForm(empty); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggle = async (id) => {
    try { await api.put(`/admin/waiters/${id}/toggle`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/waiters/${id}`); toast.success("Mesero eliminado"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="animate-fade-up" data-testid="admin-waiters-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#7248ae] mb-2">Equipo</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-[#1a0f2e] tracking-tight">Meseros</h1>
          <p className="text-[#7248ae] mt-2">Crea las cuentas con las que tus meseros entrarán a la app.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button disabled={expired} className="bg-[#3e166c] hover:bg-[#7248ae] text-white rounded-xl" data-testid="new-waiter-button">
              <Plus className="h-4 w-4 mr-2" />Nuevo mesero
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display text-2xl">Nuevo mesero</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="waiter-form">
              <div><Label>Nombre</Label><Input required value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="waiter-name-input" /></div>
              <div><Label>Correo</Label><Input type="email" required value={form.email} onChange={e=>setForm({...form,email:e.target.value})} data-testid="waiter-email-input" /></div>
              <div><Label>Contraseña</Label><Input type="password" required minLength={6} value={form.password} onChange={e=>setForm({...form,password:e.target.value})} data-testid="waiter-password-input" /></div>
              <DialogFooter><Button type="submit" className="bg-[#3e166c] hover:bg-[#7248ae] text-white" data-testid="submit-waiter-button">Crear</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="waiters-grid">
        {waiters.map((w, i) => (
          <Card key={w.id} className={`p-5 border border-[#b182ec]/30 rounded-2xl card-glow animate-fade-up stagger-${(i%6)+1}`} data-testid={`waiter-card-${w.email}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-display text-lg font-bold text-[#1a0f2e]">{w.name}</h3>
                <p className="text-sm text-[#7248ae]">{w.email}</p>
              </div>
              <Badge className={w.active ? "bg-[#10b981] hover:bg-[#10b981]/80" : "bg-[#7248ae] hover:bg-[#7248ae]/80"}>
                {w.active ? "Activo" : "Pausado"}
              </Badge>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => toggle(w.id)} disabled={expired} className="border-[#b182ec]/50" data-testid={`toggle-waiter-${w.email}`}>
                <Power className="h-4 w-4 mr-1" />{w.active ? "Pausar" : "Activar"}
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="outline" disabled={expired} className="border-[#e11d48]/40 text-[#e11d48] hover:bg-[#e11d48]/10 ml-auto" data-testid={`delete-waiter-${w.email}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Eliminar mesero?</AlertDialogTitle>
                    <AlertDialogDescription>Se eliminará la cuenta de <strong>{w.name}</strong>.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction className="bg-[#e11d48] hover:bg-[#e11d48]/80" onClick={() => remove(w.id)}>Eliminar</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
